import React, { KeyboardEvent, MouseEvent } from 'react';
import { GridDetailRowProps } from '@progress/kendo-react-grid';
import ListItem from './ListItem';
import RowSkeleton from '../../skeleton/RowSkeleton';
import HBox from '../../../layout/container/h_box/HBox';
import _ from 'lodash';
import { Keys } from '@progress/kendo-react-common';
import {
  FOCUSABLE_ELEMENTS_WITHIN_TR,
  getNodesFromContainerFromTypes,
  isDescendant,
  isElNotLinkButtonOrInput
} from '../../../../utility';
import { LIST_PROPS, LIST_STATE, POS_LIST_ITEM } from './Types';

class List extends React.Component<LIST_PROPS, LIST_STATE> {
  private displayRows: number = 5;
  private bodyHeightAdded: number = 0;
  private renderedRows: number = 10;
  private rowSkeletonConfig = [];

  state:LIST_STATE = {
    chainData: [],
    contextMenuItem: undefined,
    offset: {
      left: 0,
      top: 0
    },
    rowRenderStartIndex: 0,
    showContextMenu: false,
    top: 0
  };

  rowRefs = new Map();
  /**
   * Last selected id of ListItem
   */
  lastSelectedItemId: string;
  /**
   * Ref of scroll container
   */
  scrollContainer;
  /**
   * If there is not dom element that should be focused this property will be true
   */
  doExecuteDomItem: boolean;

  constructor(props) {
    super(props);
    const { displayDetailListRows, renderedDetailListRows } = props;
    if (displayDetailListRows || renderedDetailListRows) {
      this.displayRows = displayDetailListRows;
      this.renderedRows = renderedDetailListRows;
    }
    this.scrollContainer = React.createRef();
    this.updateRowSkeletonConfig();
  }

  componentDidUpdate(prevProps: Readonly<LIST_PROPS>, prevState: Readonly<LIST_STATE>, snapshot?: any) {
    const newChainData = (this.props?.dataItem?.subItems || []).slice(this.state.rowRenderStartIndex, this.state.rowRenderStartIndex + this.renderedRows);
    if (!_.isEqual(this.state.chainData, newChainData)) {
      this.setState({
        chainData: newChainData
      });
    }

    if (this.props.columns !== prevProps.columns) {
      this.updateRowSkeletonConfig();
    }
  }

  shouldComponentUpdate(nextProps: Readonly<LIST_PROPS>, nextState: Readonly<LIST_STATE>, nextContext: any): boolean {
    return !_.isEqual(this.props, nextProps) || !_.isEqual(this.state, nextState);
  }

  closeContextMenu = () => {
    this.setState({
      showContextMenu: false
    });
  };

  handleContextMenu = (event, dataItem) => {
    event.preventDefault();
    this.setState({
      contextMenuItem: dataItem,
      offset: {
        left: event.clientX,
        top: event.clientY
      },
      showContextMenu: true
    });
  }

  onScroll = (event) => {
    const { dataItem, rowHeight } = this.props;
    const scrollTop = event.currentTarget.scrollTop;
    const scrollRows = Math.floor(scrollTop / rowHeight);
    const translateTop = scrollRows * rowHeight;

    this.setState({
      chainData: dataItem.subItems.slice(scrollRows, scrollRows + this.renderedRows),
      rowRenderStartIndex: scrollRows,
      top: translateTop
    }, () => {
      if (this.doExecuteDomItem) {
        this.executeDomItemFocus();
      }
    });
  };

  private updateRowSkeletonConfig = () => {
    const { columns, rowHeight } = this.props;
    this.rowSkeletonConfig = columns.filter(column => !column.hidden).map(({ cellType, width }) => ({
      height: rowHeight,
      type: cellType || 'text',
      width
    }));
  };

  renderContextMenu = () => {
    const { contextMenu } = this.props;
    let menu: any = null;

    if (Array.isArray(contextMenu) && contextMenu.length) {
      const [ContextMenu, props = {}] = contextMenu;

      Object.assign(props, {
        closeMenuHandler: this.closeContextMenu,
        contextMenuItem: this.state.contextMenuItem,
        offset: this.state.offset,
        show: this.state.showContextMenu
      });

      menu = <ContextMenu {...props} />;
    }

    return menu;
  };

  /**
   * Returns item index in list by uniqueField value
   * @param {String} uniqueFieldValue
   */
  private getItemIndexByUniqueFieldValue = (uniqueFieldValue) => {
    const { dataItem, uniqueField } = this.props;
    let index;
    dataItem.subItems.every((item, ind) => {
      if (uniqueFieldValue == item[uniqueField]) {
        index = ind;
        return false;
      } else {
        return true;
      }
    });
    return index;
  }

  /**
   * Sets focus to the dom element that has an id equal to the `lastSelectedItemId` property if the element is rendered
   */
  private executeDomItemFocus = () => {
    const { dataItem: { subItems }, uniqueField } = this.props;
    if (!this.lastSelectedItemId) {
      this.lastSelectedItemId = `ID_${subItems[0][uniqueField]}`;
    }
    const domItem = document.getElementById(this.lastSelectedItemId);
    if (domItem) {
      this.doExecuteDomItem = false;
      domItem.focus();
    }
  }

  private setSelectedRowDataItem = (itemData = this.props.dataItem?.subItems?.[0], isSubItem?: boolean) => {
    itemData && this.props.onSetSelectedRowDataItem(itemData, isSubItem);
  }

  /**
   * Sets focus to first or last ListItem
   * @param {POS_LIST_ITEM} pos There can be 'first' or 'last
   */
  setFocusToTreeListItem = (pos?: POS_LIST_ITEM) => {
    const {
      uniqueField,
      dataItem: { subItems }
    } = this.props;
    const focusDataItem = subItems?.length && subItems[!pos || pos === 'first' ? 0 : subItems.length - 1];
    if (focusDataItem) {
      this.scrollToRecord(focusDataItem);
      this.lastSelectedItemId = `ID_${focusDataItem[uniqueField]}`;
      if (document.getElementById(this.lastSelectedItemId)) {
        this.executeDomItemFocus();
        this.setSelectedRowDataItem(focusDataItem, true);
      } else {
        this.doExecuteDomItem = true;
      }
    }
  }

  /**
   * Check if any of the list items have focus
   */
  isListItemFocused = () => {
    let lastSelectedIndex;
    let focusedItemId = document.activeElement?.id;
    if (!(_.isString(focusedItemId) && focusedItemId.search('ID_') !== -1)) {
      focusedItemId = this.getFocusedListItemIdByFocusedChild();
    }
    if (_.isString(focusedItemId) && focusedItemId.search('ID_') !== -1) {
      this.lastSelectedItemId = focusedItemId;
      lastSelectedIndex = this.getItemIndexByUniqueFieldValue(focusedItemId.replace('ID_', ''));
    }
    return !_.isUndefined(lastSelectedIndex);
  }

  /**
   * Returns focused list item id by child of item that have focus
   */
  getFocusedListItemIdByFocusedChild = () => {
    let listItemId;
    this.rowRefs.forEach((key, index) => {
      const row = document.getElementById(`ID_${index}`);
      if (row && isDescendant(row, document.activeElement)) {
        listItemId = row.id;
      }
    });
    return listItemId;
  }

  /**
   * Trigger if user focus last element of last child with shift-tab (form the record below)
   * @param {string} id
   */
  public setLastSelectedListItemId = (id: string):void => {
    this.lastSelectedItemId = `ID_${id}`;
  }

  /**
   * Return true if is focused element last child
   * @param {Element} focusedElement
   */
  private isFocusedElementLastChild = (focusedElement: Element):boolean => {
    const lastSelectedRow = document.getElementById(this.lastSelectedItemId);
    const lastSelectedRowElements = getNodesFromContainerFromTypes(lastSelectedRow, FOCUSABLE_ELEMENTS_WITHIN_TR, [{
      name: 'tabindex',
      value: '0'
    }]);
    let lastSelectedElement = lastSelectedRow;
    if (!_.isEmpty(lastSelectedRowElements)) {
      lastSelectedElement = _.last(lastSelectedRowElements);
    }

    return focusedElement === lastSelectedElement;
  }

  renderListView = (data: any[] = []) => {
    const {
      columns,
      contextMenu,
      rowHeight,
      onChangeItem,
      uniqueField,
      dataItem: { subItems },
      onDetailItemDoubleClick,
      dataIndex
    } = this.props;
    const hasContextMenu = Boolean(Array.isArray(contextMenu) && contextMenu.length);
    const parentId = this.props.dataItem[uniqueField];
    return (
      <div
        className="k-listview k-widget k-d-flex tp-navigation-base-tree-grid-list"
      >
        <div className="k-listview-content" data-cy="listview-content">
          {data.map((dataItem, index) => _.isEmpty(dataItem)
            ? (
              <HBox
                className="tp-sub-lvl-row"
                key={index}
              >
                <RowSkeleton
                  className="tp-sub-lvl-cell"
                  data={this.rowSkeletonConfig}
                />
              </HBox>
            )
            : (
              <ListItem
                onClick={(event: MouseEvent) => {
                  const lastSelectedIndex = `ID_${dataItem[uniqueField]}`;
                  if (lastSelectedIndex !== this.lastSelectedItemId) {
                    const nodeType: string = _.get(event.target, 'nodeName', '');
                    this.lastSelectedItemId = lastSelectedIndex;
                    if (isElNotLinkButtonOrInput(nodeType)) {
                      this.executeDomItemFocus();
                    }
                  }
                  if (_.isFunction(this.props.setLastSelectedIndex)) {
                    this.props.setLastSelectedIndex(this.props.dataItem, {
                      ...dataItem
                    });
                  }
                  this.setSelectedRowDataItem(dataItem, true);
                  if (_.isFunction(this.props.setEnableNavigation)) {
                    this.props.setEnableNavigation(false);
                  }
                }}
                onKeyDown={(e: KeyboardEvent) => {
                  const { keyCode, shiftKey } = e;
                  let dataItem: any;
                  let lastSelectedIndex = -1;
                  const focusedElement = document.activeElement;
                  const nodeType: string = _.get(focusedElement, 'nodeName').toLowerCase();
                  const focusedItemId = focusedElement?.id;

                  switch (keyCode) {
                    case Keys.up:
                    case Keys.down:
                      e.preventDefault();
                      if (_.isString(this.lastSelectedItemId)) {
                        lastSelectedIndex = this.getItemIndexByUniqueFieldValue(this.lastSelectedItemId.replace('ID_', ''));
                      }
                      if (keyCode === Keys.up && lastSelectedIndex > 0) {
                        dataItem = subItems[lastSelectedIndex - 1]; // This is the item that has to be passed to the form
                      } else if (keyCode === Keys.down && lastSelectedIndex < subItems.length - 1) {
                        dataItem = subItems[lastSelectedIndex + 1]; // This is the item that has to be passed to the form
                      }
                      if (dataItem) {
                        this.scrollToRecord(dataItem);
                        this.lastSelectedItemId = `ID_${dataItem[uniqueField]}`;
                        if (document.getElementById(this.lastSelectedItemId)) {
                          this.executeDomItemFocus();
                          this.setSelectedRowDataItem(dataItem, true);
                        } else {
                          this.doExecuteDomItem = true;
                        }
                      } else if (this.props.onKeyDownListItem) {
                        this.props.onKeyDownListItem(e, true, this.props.dataItem);
                      }
                      break;
                    case Keys.tab:
                      if (_.isString(focusedItemId) && focusedItemId.search('ID_') !== -1) {
                        lastSelectedIndex = this.getItemIndexByUniqueFieldValue(focusedItemId.replace('ID_', ''));
                        if (shiftKey) {
                          if (lastSelectedIndex === 0) {
                            if (_.isFunction(this.props.setLastSelectedIndex)) {
                              this.props.setLastSelectedIndex(this.props.dataItem);
                            }
                            if (_.isFunction(this.props.setEnableNavigation)) {
                              this.props.setEnableNavigation(true);
                            }
                          } else {
                            this.lastSelectedItemId = `ID_${subItems[lastSelectedIndex - 1][uniqueField]}`;
                            dataItem = subItems[lastSelectedIndex - 1];
                            this.scrollToRecord(dataItem);
                          }
                        } else {
                          this.lastSelectedItemId = focusedItemId;
                          dataItem = subItems[lastSelectedIndex + 1];
                          if (dataItem) {
                            this.scrollToRecord(dataItem);
                          }
                        }
                      } else if (this.isFocusedElementLastChild(focusedElement) && !shiftKey) {
                        const { target } = e;
                        const focusedElement = target as any;
                        const idOfSelectedRecordValue = focusedElement?.offsetParent?.id;
                        lastSelectedIndex = this.getItemIndexByUniqueFieldValue(idOfSelectedRecordValue.replace('ID_', ''));
                        if (lastSelectedIndex === subItems.length - 1) {
                          const isLastIndexOfLastRecord = this.props.isLastIndexOfLastRecord();
                          if (!isLastIndexOfLastRecord) {
                            this.props.scrollToLeft();
                          }
                          this.props.onKeyDownListItem(e, !isLastIndexOfLastRecord, this.props.dataItem, true);
                        } else {
                          this.props.scrollToLeft();
                          this.lastSelectedItemId = `ID_${subItems[lastSelectedIndex + 1][uniqueField]}`;
                          dataItem = subItems[lastSelectedIndex + 1];
                          this.scrollToRecord(dataItem);
                        }
                      }
                      break;
                    case Keys.pageDown:
                      e.preventDefault();
                      dataItem = subItems[subItems.length - 1];
                      if (dataItem) {
                        this.scrollToRecord(dataItem);
                        this.lastSelectedItemId = `ID_${dataItem[uniqueField]}`;
                        if (document.getElementById(this.lastSelectedItemId)) {
                          this.executeDomItemFocus();
                        } else {
                          this.doExecuteDomItem = true;
                        }
                        this.setSelectedRowDataItem(dataItem, true);
                      }
                      break;
                    case Keys.pageUp:
                      e.preventDefault();
                      dataItem = subItems[0];
                      if (dataItem) {
                        this.scrollToRecord(dataItem);
                        this.lastSelectedItemId = `ID_${dataItem[uniqueField]}`;
                        if (document.getElementById(this.lastSelectedItemId)) {
                          this.executeDomItemFocus();
                        } else {
                          this.doExecuteDomItem = true;
                        }
                        this.setSelectedRowDataItem(dataItem, true);
                      }
                      break;
                    case Keys.enter:
                      if (_.isFunction(this.props.onDetailItemDoubleClick) && isElNotLinkButtonOrInput(nodeType)) {
                        lastSelectedIndex = this.getItemIndexByUniqueFieldValue(this.lastSelectedItemId.replace('ID_', ''));
                        const dataItem = subItems[lastSelectedIndex];
                        this.props.onDetailItemDoubleClick(dataItem);
                      }
                      break;
                    case Keys.end:
                    case Keys.home:
                      e.preventDefault();
                      break;
                    default:
                      break;
                  }
                }}
                onKeyUp={(e: KeyboardEvent) => {
                  const { shiftKey, keyCode } = e;
                  const focusedElement = document.activeElement;
                  const role: string = _.get(focusedElement, 'attributes')['role']?.value.toLowerCase();
                  const focusedItemId = focusedElement?.id;

                  if (shiftKey && _.isString(focusedItemId) && focusedItemId.search('ID_') === -1 && this.isFocusedElementLastChild(focusedElement)) {
                    this.lastSelectedItemId = `ID_${subItems[subItems.length - 1][uniqueField]}`;
                    if (_.isFunction(this.props.setLastSelectedIndex)) {
                      this.props.setLastSelectedIndex(this.props.dataItem);
                    }
                    if (_.isFunction(this.props.setEnableNavigation)) {
                      this.props.setEnableNavigation(false);
                    }
                  }
                  if (keyCode === Keys.tab && role === 'row') {
                    const { dataItem } = this.props;
                    const { subItems } = dataItem;
                    const lastSelectedIndex = this.getItemIndexByUniqueFieldValue(this.lastSelectedItemId.replace('ID_', ''));
                    this.setSelectedRowDataItem(subItems[lastSelectedIndex], true);
                  } else if (keyCode === Keys.home || keyCode === Keys.end) {
                    const focusedRow = document.getElementById(this.lastSelectedItemId);
                    const elements = getNodesFromContainerFromTypes(focusedRow, FOCUSABLE_ELEMENTS_WITHIN_TR, [{
                      name: 'tabindex',
                      value: '0'
                    }]);
                    if (!_.isEmpty(elements)) {
                      const nextFocusedElement = Keys.home === keyCode ? _.head(elements) : _.last(elements);
                      if (nextFocusedElement) {
                        nextFocusedElement.focus();
                      }
                    }
                  }
                }}
                columns={columns}
                dataItem={{ ...dataItem, parentId }}
                parentDataIndex={dataIndex}
                key={dataItem[uniqueField]}
                onChangeItem={onChangeItem}
                onContextMenu={hasContextMenu ? this.handleContextMenu : undefined}
                onDetailItemDoubleClick={onDetailItemDoubleClick}
                ref={(listItem) => {
                  const refId = dataItem[uniqueField];
                  if (listItem) {
                    this.rowRefs.set(refId, listItem);
                  } else {
                    this.rowRefs.delete(refId);
                  }
                }}
                rowHeight={rowHeight}
                uniqueField={uniqueField}
              />
            ))}
        </div>
      </div>
    );
  };

  /**
   * Checks if there is item that has this 'recordIndex'
   * @param {Number} recordIndex
   */
  isRecordOutOfView = (recordIndex) => {
    const { rowHeight } = this.props;
    const { offsetHeight, scrollTop } = this.scrollContainer.current;
    const recordPosTop = recordIndex * rowHeight;
    const recordPosBottom = recordPosTop + rowHeight;
    return recordPosTop < scrollTop || recordPosBottom > scrollTop + offsetHeight;
  }

  /**
   *  Scrolls container to dataItem if it is out of view, preserves horizontal scroll position.
   */
  private scrollToRecord = (dataItem: any): boolean => {
    const { uniqueField, rowHeight } = this.props;
    const recordIndex = this.getItemIndexByUniqueFieldValue(dataItem[uniqueField]);
    const scrolled = this.isRecordOutOfView(recordIndex);
    if (scrolled) {
      const container = this.scrollContainer.current;
      const scrollTop = (recordIndex - 2) * rowHeight;
      container.scrollTo(container.scrollLeft, scrollTop);
    }
    return scrolled;
  };

  render() {
    const data = this.props.dataItem.subItems;
    const allNumberOfRows = data?.length;
    const { renderedRows, displayRows, bodyHeightAdded, props } = this;
    const { rowHeight } = props;
    const chainData = this.state.chainData.length ? this.state.chainData : data?.slice(0, renderedRows);
    const { top } = this.state;
    const scrollBodyHeight = (rowHeight * allNumberOfRows) + bodyHeightAdded;
    const displayPageHeight = (rowHeight * displayRows) + bodyHeightAdded;
    const scrollClass = (displayPageHeight < scrollBodyHeight) ? 'tp-scroll' : '';

    return (
      <div
        className="tp-expanded-list-wrapper"
        style={{
          overflow: 'auto',
          maxHeight: displayPageHeight,
          width: '100%'
        }}
        onScroll={this.onScroll}
        ref={this.scrollContainer}
      >
        {this.renderContextMenu()}
        <div
          className={scrollClass}
          style={{
            position: 'relative',
            height: scrollBodyHeight
          }}
        >
          <div
            style={{
              position: 'absolute',
              transform: `translate(0, ${top}px)`,
              width: '100%'
            }}
          >
            {this.renderListView(chainData)}
          </div>
        </div>
      </div>
    );
  }
}

export default List;
